import React from 'react';
import { useTranslation } from 'react-i18next';
import errorImage from '@/assets/images/error.webp';
import { Button } from '@/components/Button';
import { Close } from '@/components/Icons/Close';
import { Modal } from '@/components/Modal';
import { useSwipeToClose } from '@/hooks/useSwipeToClose';
import { ButtonVariant, ModalVariant, Size } from '@/types/components';

interface Props {
  isOpen: boolean;
  title: string;
  description?: string;
  disclaimer?: string;
  image?: string;
  cancelLabel?: string;
  confirmLabel?: string;
  confirmVariant?: ButtonVariant;
  confirmClassName?: string;
  simpleView?: boolean;
  hideCancel?: boolean;
  isPending?: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

export const ConfirmModal: React.FC<Props> = ({
  isOpen,
  title,
  description,
  disclaimer,
  image,
  cancelLabel,
  confirmLabel,
  confirmVariant = ButtonVariant.Primary,
  confirmClassName = '',
  simpleView = false,
  hideCancel = false,
  isPending = false,
  onClose,
  onConfirm,
}) => {
  const { t } = useTranslation();
  const { sheetRef, swipeProps } = useSwipeToClose(onClose);

  const modalClassName = simpleView
    ? 'md:h-auto md:w-[400px] md:max-h-[calc(100svh-80px)] md:rounded-2xl'
    : 'md:h-auto md:w-[400px] md:max-h-[calc(100svh-80px)]';

  const actions = (
    <>
      {!hideCancel && (
        <Button
          variant={ButtonVariant.Secondary}
          size={Size.Large}
          onClick={onClose}
          className="flex-1"
        >
          {cancelLabel ?? t('global.cancel')}
        </Button>
      )}
      <Button
        variant={confirmVariant}
        size={Size.Large}
        isLoading={isPending}
        onClick={onConfirm}
        className={`flex-1 ${confirmClassName}`}
      >
        {confirmLabel ?? t('global.confirm')}
      </Button>
    </>
  );

  return (
    <Modal
      isOpen={isOpen}
      label={title}
      variant={ModalVariant.BottomSheet}
      className={modalClassName}
      contentRef={sheetRef}
      onClose={onClose}
    >
      {simpleView && (
        <div className="relative flex flex-col gap-6 p-6 pb-[calc(24px+env(safe-area-inset-bottom))] max-md:pb-[calc(32px+env(safe-area-inset-bottom))]">
          <div
            {...swipeProps}
            className="absolute inset-x-0 top-0 flex h-6 justify-center md:hidden max-md:cursor-grab max-md:touch-none max-md:active:cursor-grabbing"
          >
            <div className="mt-2 h-[3px] w-10 rounded-full bg-drag-handle" />
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('global.close')}
            className="absolute top-3 right-3 hidden size-4 cursor-pointer items-center justify-center text-foreground md:flex"
          >
            <Close />
          </button>
          <div className="flex flex-col gap-4 max-md:items-center">
            {image && (
              <img
                src={image}
                alt=""
                width={100}
                height={100}
                className="size-[100px] md:hidden"
              />
            )}
            <div className="flex flex-col gap-2 max-md:max-w-[316px] max-md:items-center max-md:text-center">
              <h2 className="text-[18px]/[24px] font-semibold text-foreground md:text-[20px]/[28px] md:font-medium">
                {title}
              </h2>
              {description && (
                <p className="text-body-14 text-muted-foreground">
                  {description}
                </p>
              )}
              {disclaimer && (
                <p className="text-body-14 text-destructive">{disclaimer}</p>
              )}
            </div>
          </div>
          <div className="flex gap-3">{actions}</div>
        </div>
      )}

      {!simpleView && (
        <>
          <div
            {...swipeProps}
            className="flex shrink-0 flex-col max-md:cursor-grab max-md:touch-none max-md:active:cursor-grabbing"
          >
            <div className="mx-auto mt-2 h-[3px] w-10 rounded-full bg-drag-handle md:hidden" />
            <div className="flex items-center justify-end px-4 pt-2 pb-0 md:px-5 md:pt-5">
              <button
                type="button"
                onClick={onClose}
                aria-label={t('global.close')}
                className="hidden size-4 shrink-0 cursor-pointer items-center justify-center text-foreground md:flex"
              >
                <Close />
              </button>
            </div>
          </div>

          <div className="flex flex-1 flex-col items-center gap-4 px-4 pt-4 md:px-10 md:pt-2">
            <img
              src={image ?? errorImage}
              alt=""
              width={100}
              height={100}
              className="size-[100px]"
            />
            <div className="flex max-w-[316px] flex-col items-center gap-2 text-center">
              <h2 className="text-callout-16 text-foreground">{title}</h2>
              {description && (
                <p className="text-body-14 text-muted-foreground">
                  {description}
                </p>
              )}
              {disclaimer && (
                <p className="text-body-14 text-destructive">{disclaimer}</p>
              )}
            </div>
          </div>

          <div className="flex shrink-0 gap-3 bg-background px-4 pt-6 pb-[calc(24px+env(safe-area-inset-bottom))] md:p-5">
            {actions}
          </div>
        </>
      )}
    </Modal>
  );
};
